// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { PAGE_PERMISSIONS as P, permissionMatches } from "@/lib/auth/page-permissions";

// Shared permission-tree definitions and helpers, consumed by both the user permission tabs
// (user-permissions-tab / user-bo-permissions-tab) and the group permission tabs
// (group-permissions-tab / group-bo-permissions-tab). Kept in one place so the two features
// render and reconcile the exact same trees.

// ---------------------------------------------------------------------------
// Product (API) permissions
// ---------------------------------------------------------------------------

// Product (API) permissions, gathered from the backends and the product clients:
//   - backend-main:   `enhance_endpoint(permissions=[...])` in fsim/apps/api
//   - backend-legacy: `@require_auth([...])` / `check_permissions([...])` in focal/app/api
//   - web-focal-os:   `checkPermission(...)` / `selectHasPermission(...)` across apps/ + libs/
// Format is `resource:action[:modifier]`; `*:*` (a.k.a. legacy `*:*:*`) is full access.
//
// Two things to know before adding rows here:
//   - A section's header switch IS `${prefix}:*`, so a resource whose only grantable form is the
//     wildcard needs a section with no rows (blackhat, deployment, ims, payment, …). Endpoints that
//     *require* `resource:*:*` are satisfied by `resource:*`, which is why no `:*:*` rows exist.
//   - Rows sourced only from web-focal-os gate UI, not endpoints — revoking them hides a control
//     without closing anything server-side. Their descriptions say "(client-side)".
// Section `label`s are i18n keys (resolved via t); node rows show their raw permission string
// verbatim. PERMISSION_DESCRIPTIONS (hover) are still literal English (pending translation).
export interface ProductPermissionSection {
  label: string;
  prefix: string;
  permissions: string[];
}

export const FULL_ACCESS = "*:*";

export const REGULAR_SECTIONS: ProductPermissionSection[] = [
  {
    label: "users.permissions.labels.action",
    prefix: "action",
    permissions: [
      "action:backroom",
      "action:filter:r",
      "action:home",
      "action:ims:add-location",
      "action:ims:add-location:*",
      "action:ims:edit",
      "action:ims:product-edit",
      "action:ims:view",
      "action:inventory-update",
      "action:inventory-update:*",
      "action:manager-approval-inventory:a",
      "action:metrics",
      "action:picklist",
      "action:r",
      "action:settings",
      "action:sort:r",
      "action:tasks",
      "action:tasks:*",
      "action:tasks:a",
      "action:zero-out-inventory:*",
      "action:zero-out-location",
      "action:zero-out-location:*",
    ],
  },
  { label: "users.permissions.labels.adaOrder", prefix: "adaorder", permissions: ["adaorder:create"] },
  {
    label: "users.permissions.labels.adaPogs",
    prefix: "adapogs",
    permissions: ["adapogs:create", "adapogs:delete", "adapogs:edit", "adapogs:send", "adapogs:view"],
  },
  {
    label: "users.permissions.labels.backofficeLegacy",
    prefix: "backoffice",
    permissions: ["backoffice:edit", "backoffice:import", "backoffice:view"],
  },
  // Wildcard-only: the header switch grants `blackhat:*`.
  { label: "users.permissions.labels.blackhat", prefix: "blackhat", permissions: [] },
  // Wildcard-only: legacy requires `deployment:*:*`, satisfied by `deployment:*`.
  { label: "users.permissions.labels.deployment", prefix: "deployment", permissions: [] },
  { label: "users.permissions.labels.developer", prefix: "developer", permissions: ["developer:export"] },
  {
    label: "users.permissions.labels.exec",
    prefix: "exec",
    permissions: ["exec:billing:r", "exec:billing:w", "exec:metrics:r"],
  },
  // Wildcard-only: legacy requires `factory-qa:*:*`, satisfied by `factory-qa:*`.
  { label: "users.permissions.labels.factoryQa", prefix: "factory-qa", permissions: [] },
  // `field:a` covers ONLY the `field` prefix — the `field-*` sections below are separate resources.
  { label: "users.permissions.labels.fieldTool", prefix: "field", permissions: ["field:a"] },
  {
    label: "users.permissions.labels.fieldDeployment",
    prefix: "field-deployment",
    permissions: ["field-deployment:mode:a", "field-deployment:upload-images:a"],
  },
  {
    label: "users.permissions.labels.fieldIms",
    prefix: "field-ims",
    permissions: ["field-ims:requests:a"],
  },
  {
    label: "users.permissions.labels.fieldMaintenanceVisit",
    prefix: "field-maintenance-visit",
    permissions: ["field-maintenance-visit:review:*", "field-maintenance-visit:review:a"],
  },
  { label: "users.permissions.labels.fieldQa", prefix: "field-qa", permissions: ["field-qa:export-batch"] },
  {
    label: "users.permissions.labels.fieldSetup",
    prefix: "field-setup",
    permissions: ["field-setup:config:set-restricted-configs:a"],
  },
  {
    label: "users.permissions.labels.fieldStatus",
    prefix: "field-status",
    permissions: ["field-status:create-tasks"],
  },
  {
    label: "users.permissions.labels.fieldStore",
    prefix: "field-store",
    permissions: ["field-store:config:a", "field-store:config:r", "field-store:ota:a"],
  },
  {
    label: "users.permissions.labels.fieldTasks",
    prefix: "field-tasks",
    permissions: [
      "field-tasks:assign",
      "field-tasks:context-menu",
      "field-tasks:create:esl-install",
      "field-tasks:create:store-renov",
      "field-tasks:rejected-filter",
    ],
  },
  {
    label: "users.permissions.labels.impact",
    prefix: "impact",
    permissions: [
      "impact:adaorder:a",
      "impact:at_settings:*",
      "impact:at_settings:a",
      "impact:export",
      "impact:health",
      "impact:health:view",
      "impact:home:r",
      "impact:import",
      "impact:import:*",
      "impact:import:r",
      "impact:leaderboards:r",
      "impact:metrics:r",
      "impact:notifications:*",
      "impact:notifications:a",
      "impact:r",
      "impact:recommendations:r",
      "impact:schedule:a",
      "impact:settings:r",
      "impact:theft-alerting-exclusions:a",
      "impact:theft-alerting-exclusions:r",
      "impact:theft-alerting-settings:*",
      "impact:theft-alerting-settings:edit",
      "impact:theft-alerting:a",
      "impact:theft-alerting:r",
      "impact:users:*",
      "impact:users:a",
    ],
  },
  // Wildcard-only: legacy requires `ims:*:*`, satisfied by `ims:*`.
  { label: "users.permissions.labels.ims", prefix: "ims", permissions: [] },
  // Wildcard-only: legacy requires `ims-addresses:*:*`, satisfied by `ims-addresses:*`.
  { label: "users.permissions.labels.imsAddresses", prefix: "ims-addresses", permissions: [] },
  // Wildcard-only: the header switch grants `label-print:*`.
  { label: "users.permissions.labels.labelPrint", prefix: "label-print", permissions: [] },
  {
    label: "users.permissions.labels.messages",
    prefix: "messages",
    permissions: ["messages:definition", "messages:definition:a", "messages:send"],
  },
  // Wildcard-only: legacy requires both `ops-tasks:*` and `ops-tasks:*:*`, satisfied by `ops-tasks:*`.
  { label: "users.permissions.labels.opsTasks", prefix: "ops-tasks", permissions: [] },
  {
    label: "users.permissions.labels.otaVersions",
    prefix: "ota-versions",
    permissions: ["ota-versions:register:r"],
  },
  // Wildcard-only: legacy requires both `payment:*` and `payment:*:*`, satisfied by `payment:*`.
  { label: "users.permissions.labels.payment", prefix: "payment", permissions: [] },
  // Wildcard-only: the header switch grants `payroll:*`.
  { label: "users.permissions.labels.payroll", prefix: "payroll", permissions: [] },
  {
    label: "users.permissions.labels.productRecognition",
    prefix: "product-recognition",
    permissions: [
      "product-recognition:classifications:*",
      "product-recognition:crop-scans",
      "product-recognition:products",
    ],
  },
  {
    label: "users.permissions.labels.psi",
    prefix: "psi",
    permissions: ["psi:copy-mod:a", "psi:locked-mods:a", "psi:pause-mods:a", "psi:unpause-mods:a"],
  },
  {
    label: "users.permissions.labels.review",
    prefix: "review",
    permissions: ["review:disputes:a", "review:metrics", "review:queue:a", "review:r", "review:tickets:*"],
  },
  {
    label: "users.permissions.labels.settings",
    prefix: "settings",
    permissions: [
      "settings:messaging:*",
      "settings:schedule:*",
      "settings:schedule:a",
      "settings:stores",
      "settings:stores:*",
      "settings:stores:a",
      "settings:users:*",
      "settings:users:a",
    ],
  },
  {
    label: "users.permissions.labels.setup",
    prefix: "setup",
    permissions: [
      "setup:create:a",
      "setup:debug:a",
      "setup:device:*",
      "setup:device:a",
      "setup:device:r",
      "setup:device_config:a",
      "setup:tasks:a",
      "setup:tasks:r",
    ],
  },
  // Not a feature toggle: `stores:all:a` makes validate_store_access() return true for EVERY store,
  // replacing the user's assigned-store list. Kept in its own section so it can't be mistaken for one.
  { label: "users.permissions.labels.storeAccess", prefix: "stores", permissions: ["stores:all:a"] },
  {
    label: "users.permissions.labels.users",
    prefix: "users",
    permissions: ["users:create", "users:delete", "users:edit", "users:view"],
  },
  { label: "users.permissions.labels.vendors", prefix: "vendors", permissions: ["vendors:create", "vendors:delete"] },
];

// Brief, human descriptions derived from the endpoints each permission guards (see the backends above).
export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  "action:backroom": "Manage backroom inventory labels & printing",
  "action:filter:r": "Use the task filters (client-side)",
  "action:home": "View the Action tool home screen",
  "action:ims:add-location": "Create inventory storage locations",
  "action:ims:add-location:*": "All add-location actions — the form the client checks (client-side)",
  "action:manager-approval-inventory:a": "Manager-approval inventory task tally",
  "action:ims:edit": "Edit IMS actions on products",
  "action:ims:product-edit": "Add, edit & pair products",
  "action:ims:view": "View IMS inventory & products",
  "action:inventory-update": "Update inventory presence & stock",
  "action:inventory-update:*": "All inventory-update actions",
  "action:metrics": "View the store metrics dashboard",
  "action:picklist": "Create & manage picklists",
  "action:r": "Read Action tool home data",
  "action:settings": "View & change store action settings",
  "action:sort:r": "Use the task sorting controls (client-side)",
  "action:tasks": "List, create, assign & resolve tasks",
  "action:tasks:*": "All task actions",
  "action:tasks:a": "POG compliance & scan-in actions",
  "action:zero-out-inventory:*": "Clear the ENTIRE store's inventory (client-side)",
  "action:zero-out-location": "Zero out inventory in a location",
  "action:zero-out-location:*": "All zero-out-location actions",
  "adaorder:create": "Create AdaOrders (supply orders)",
  "adapogs:create": "Upload & schedule adaptive planograms",
  "adapogs:delete": "Delete adaptive planograms",
  "adapogs:edit": "Update & restore adaptive planograms",
  "adapogs:send": "Create action tasks from planograms",
  "adapogs:view": "List & export adaptive planograms",
  "backoffice:edit": "Edit legacy backoffice data",
  "backoffice:import": "Run assisted imports",
  "backoffice:view": "View backoffice settings & context",
  "developer:export": "Export reports & raw data",
  "field-deployment:mode:a": "Change the store's deployment mode (client-side)",
  "field-deployment:upload-images:a": "Upload deployment images (client-side)",
  "field-ims:requests:a": "Create & update IMS requests",
  "field-maintenance-visit:review:*": "All maintenance-visit review actions",
  "field-maintenance-visit:review:a": "Submit & update maintenance visit summaries",
  "field-qa:export-batch": "Export a camera-QA batch as CSV (client-side)",
  "field-setup:config:set-restricted-configs:a": "Set restricted store configs (client-side)",
  "field-status:create-tasks": "Create tasks from the Status page (client-side)",
  "field-store:config:a": "Change store configuration (client-side)",
  "field-store:config:r": "View store configuration commands (client-side)",
  "field-store:ota:a": "Push OTA updates to a store (client-side)",
  "field-tasks:assign": "Assign field tasks (client-side)",
  "field-tasks:context-menu": "Use the task-card action menu (client-side)",
  "field-tasks:create:esl-install": "Create ESL-install tasks (client-side)",
  "field-tasks:create:store-renov": "Create store-renovation tasks (client-side)",
  "field-tasks:rejected-filter": "Filter tasks for rejected ones",
  "exec:billing:r": "View executive billing & budgets",
  "exec:billing:w": "Edit executive billing & budgets",
  "exec:metrics:r": "View executive metrics & KPIs",
  "field:a": "Field tool access",
  "impact:adaorder:a": "Start, cancel & finalize orders",
  "impact:at_settings:*": "All Action-tool settings",
  "impact:at_settings:a": "Edit Action-tool store settings",
  "impact:export": "View export request details",
  "impact:health": "View & edit health metrics",
  "impact:health:view": "View health overview & details",
  "impact:home:r": "View the Impact home dashboard (client-side)",
  "impact:import": "View import requests & status",
  "impact:import:*": "All import actions",
  "impact:import:r": "Export out-of-stock data",
  "impact:leaderboards:r": "View associate leaderboards",
  "impact:metrics:r": "View Impact / out-of-stock metrics",
  "impact:notifications:*": "Manage notifications & messaging",
  "impact:notifications:a": "Edit store notification settings (client-side)",
  "impact:r": "Export checkout-queue / home data",
  "impact:recommendations:r": "View recommendations (client-side)",
  "impact:schedule:a": "Edit people-management schedules (client-side)",
  "impact:settings:r": "View store settings (client-side)",
  "impact:theft-alerting-exclusions:a": "Create & delete theft-alert exclusions",
  "impact:theft-alerting-exclusions:r": "View theft-alert exclusions",
  "impact:theft-alerting-settings:*": "All theft-alerting settings (client-side)",
  "impact:theft-alerting-settings:edit": "Edit theft-alert price threshold",
  "impact:theft-alerting:a": "Update theft-detection status",
  "impact:theft-alerting:r": "View theft detections & metrics",
  "impact:users:*": "Full Impact user management",
  "impact:users:a": "Edit Impact users",
  "messages:definition": "Create, update & delete message definitions",
  "messages:definition:a": "Manage message definitions",
  "messages:send": "Send messages",
  "ota-versions:register:r": "Register a new OTA version",
  "product-recognition:classifications:*": "Manage product classifications (client-side)",
  "product-recognition:crop-scans": "Manage product crop scans",
  "product-recognition:products": "Query products by barcode",
  "psi:copy-mod:a": "Copy mods between stores (client-side)",
  "psi:locked-mods:a": "Lock & unlock mods",
  "psi:pause-mods:a": "Pause mods for maintenance",
  "psi:unpause-mods:a": "Unpause mods after maintenance",
  "review:disputes:a": "Manage disputed QA items",
  "review:metrics": "View review-queue metrics",
  "review:queue:a": "Process the review / labeling queue",
  "review:r": "Access the AT-Review app (client-side)",
  "review:tickets:*": "Full access to review tickets",
  "settings:messaging:*": "All messaging definitions & templates",
  "settings:schedule:*": "All camera / device schedules",
  "settings:schedule:a": "Edit device & camera schedules",
  "settings:stores": "Create new stores",
  "settings:stores:*": "Full store configuration",
  "settings:stores:a": "Edit store settings",
  "settings:users:*": "Full user management",
  "settings:users:a": "Edit user accounts & groups",
  "setup:create:a": "Configure store devices / hardware",
  "setup:debug:a": "View the device debug timeline",
  "setup:device:*": "All device actions",
  "setup:device:a": "Replace, reset & deactivate devices; edit camera metadata & batches",
  "setup:device:r": "View devices, batches, streams, camera images & FOV recommendations",
  "setup:device_config:a": "Change device config & OTA version; run device commands",
  "setup:tasks:a": "Create, assign, snooze & resolve store ops tasks",
  "setup:tasks:r": "View store ops tasks",
  "stores:all:a": "Access EVERY store, replacing the user's assigned-store list",
  "users:create": "Create new users",
  "users:delete": "Delete users",
  "users:edit": "Edit user data",
  "users:view": "View users & details",
  "vendors:create": "Create a vendor",
  "vendors:delete": "Deactivate a vendor",
};

// Every product-permission string the tree knows how to render — used to scope a principal's grants to
// product permissions (and away from `backoffice:<section>` BO permissions handled by the BO tree).
export const KNOWN_PRODUCT_PERMISSIONS = new Set<string>([
  FULL_ACCESS,
  ...REGULAR_SECTIONS.flatMap((section) => [`${section.prefix}:*`, ...section.permissions]),
]);

const FULL_WILDCARDS = new Set(["*", "*:*", "*:*:*"]);

// Does a granted product permission satisfy `required`? Full wildcards match anything; `resource:*`
// matches that resource; otherwise an exact match. (Backend also honours r/a/* hierarchy — not modelled here.)
export function productPermissionMatches(granted: string, required: string): boolean {
  if (granted === required) return true;
  if (FULL_WILDCARDS.has(granted)) return true;
  if (granted.endsWith(":*")) {
    const prefix = granted.slice(0, -2);
    return required === prefix || required.startsWith(`${prefix}:`);
  }
  return false;
}

// Collapse permissions already covered by a broader one in the same set so two sets that grant the
// same access compare equal.
export function canonicalizeProduct(permissions: Iterable<string>): Set<string> {
  const all = [...permissions];
  return new Set(all.filter((p) => !all.some((q) => q !== p && productPermissionMatches(q, p))));
}

export function productPermissionSetsEqual(a: Set<string>, b: Set<string>): boolean {
  const ca = canonicalizeProduct(a);
  const cb = canonicalizeProduct(b);
  return ca.size === cb.size && [...ca].every((permission) => cb.has(permission));
}

// Normalise legacy full wildcards (`*`, `*:*:*`) to the canonical `*:*` so the Full access switch reflects them.
export function normalizeProduct(permission: string): string {
  return FULL_WILDCARDS.has(permission) ? FULL_ACCESS : permission;
}

// ---------------------------------------------------------------------------
// Backoffice (page) permissions
// ---------------------------------------------------------------------------

// Section/node `label`s are i18n keys, resolved via t(). The hover descriptions in BO_NODE_DESCRIPTIONS
// are still literal English (pending translation). `debugOnly` rows/sections map to pages that are
// hidden unless debug mode is enabled.
export interface BoPermissionNode {
  label: string;
  view: string;
  edit?: string;
  debugOnly?: boolean;
}
export interface BoPermissionSection {
  label: string;
  // Wildcard prefix for the section-level master switches: `${prefix}:*` / `${prefix}:*:edit`.
  prefix: string;
  debugOnly?: boolean;
  nodes: BoPermissionNode[];
}

export const BO_PERMISSION_SECTIONS: BoPermissionSection[] = [
  {
    label: "users.permissions.labels.approvals",
    prefix: "backoffice:approvals",
    debugOnly: true,
    nodes: [{ label: "users.permissions.labels.approvals", view: P.approvals, edit: P.approvalsEdit }],
  },
  {
    label: "users.permissions.labels.chains",
    prefix: "backoffice:chains",
    nodes: [
      { label: "users.permissions.labels.chains", view: P.chains, edit: P.chainsEdit },
      { label: "users.permissions.labels.regions", view: P.chainsRegions, edit: P.chainsRegionsEdit },
      { label: "users.permissions.labels.settings", view: P.chainsSettings, edit: P.chainsSettingsEdit },
      {
        label: "users.permissions.labels.cutOffTimes",
        view: P.chainsCutOffTimes,
        edit: P.chainsCutOffTimesEdit,
      },
      { label: "users.permissions.labels.activity", view: P.chainsActivity },
    ],
  },
  {
    label: "users.permissions.labels.stores",
    prefix: "backoffice:stores",
    nodes: [
      { label: "users.permissions.labels.stores", view: P.stores, edit: P.storesEdit },
      { label: "users.permissions.labels.operations", view: P.storesOperations, edit: P.storesOperationsEdit },
      { label: "users.permissions.labels.health", view: P.storesHealth },
      { label: "users.permissions.labels.hardware", view: P.storesHardware, edit: P.storesHardwareEdit },
      { label: "users.permissions.labels.devices", view: P.storesDevices },
      { label: "users.permissions.labels.schedule", view: P.storesSchedule, edit: P.storesScheduleEdit },
      {
        label: "users.permissions.labels.cutOffTimes",
        view: P.storesCutOffTimes,
        edit: P.storesCutOffTimesEdit,
      },
      { label: "users.permissions.labels.map", view: P.storesMap, edit: P.storesMapEdit },
      { label: "users.permissions.labels.settings", view: P.storesSettings, edit: P.storesSettingsEdit },
      { label: "users.permissions.labels.activity", view: P.storesActivity },
    ],
  },
  {
    label: "users.permissions.labels.settings",
    prefix: "backoffice:settings",
    nodes: [
      { label: "users.permissions.labels.settings", view: P.settings },
      { label: "users.permissions.labels.chainValues", view: P.settingsChains, edit: P.settingsChainsEdit },
      { label: "users.permissions.labels.storeValues", view: P.settingsStores, edit: P.settingsStoresEdit },
      { label: "users.permissions.labels.activity", view: P.settingsActivity },
    ],
  },
  {
    label: "users.permissions.labels.iam",
    prefix: "backoffice:iam",
    nodes: [
      { label: "users.permissions.labels.users", view: P.iamUsers, edit: P.iamUsersEdit },
      { label: "users.permissions.labels.groups", view: P.iamGroups, edit: P.iamGroupsEdit },
      { label: "users.permissions.labels.vendors", view: P.iamVendors, edit: P.iamVendorsEdit },
      { label: "users.permissions.labels.permissions", view: P.iamPermissions, debugOnly: true },
      { label: "users.permissions.labels.sso", view: P.iamSso, debugOnly: true },
    ],
  },
  {
    label: "users.permissions.labels.ingress",
    prefix: "backoffice:ingress",
    nodes: [
      { label: "users.permissions.labels.imports", view: P.ingressImports, edit: P.ingressImportsEdit },
      {
        label: "users.permissions.labels.transformations",
        view: P.ingressTransformations,
        edit: P.ingressTransformationsEdit,
      },
    ],
  },
  {
    label: "users.permissions.labels.octo",
    prefix: "backoffice:octo",
    nodes: [
      { label: "users.permissions.labels.rules", view: P.octoRules, edit: P.octoRulesEdit },
      { label: "users.permissions.labels.visualizer", view: P.octoVisualizer, debugOnly: true },
    ],
  },
  {
    label: "users.permissions.labels.hardware",
    prefix: "backoffice:hardware",
    nodes: [
      { label: "users.permissions.labels.devices", view: P.hardwareDevices },
      { label: "users.permissions.labels.firmware", view: P.hardwareFirmware, edit: P.hardwareFirmwareEdit },
    ],
  },
];

// Every backoffice-permission string the BO tree renders as a switch — the slice of a principal's
// permissions it owns. On save we resend the full set: everything NOT managed here (the product/app
// slice and any unknown grants, preserved untouched) plus this tree's enabled switches.
export const MANAGED_BO_PERMISSIONS = new Set<string>([
  "backoffice:*",
  "backoffice:*:edit",
  ...BO_PERMISSION_SECTIONS.flatMap((section) => [
    `${section.prefix}:*`,
    `${section.prefix}:*:edit`,
    ...section.nodes.flatMap((node) => (node.edit ? [node.view, node.edit] : [node.view])),
  ]),
]);

// Drop permissions already covered by a broader one in the same set (e.g. `backoffice:chains:edit`
// when `backoffice:*:edit` is present), so two sets that grant the same access compare equal.
export function canonicalizeBo(permissions: Iterable<string>): Set<string> {
  const all = [...permissions];
  return new Set(all.filter((p) => !all.some((q) => q !== p && permissionMatches(q, p))));
}

export function boPermissionSetsEqual(a: Set<string>, b: Set<string>): boolean {
  const ca = canonicalizeBo(a);
  const cb = canonicalizeBo(b);
  return ca.size === cb.size && [...ca].every((permission) => cb.has(permission));
}

// Brief descriptions of what each backoffice page permission grants, keyed by the view permission.
export const BO_NODE_DESCRIPTIONS: Record<string, string> = {
  [P.approvals]: "Review & action pending approvals",
  [P.chains]: "Chain list & details",
  [P.chainsRegions]: "Regions within a chain",
  [P.chainsSettings]: "Chain-level setting values",
  [P.chainsCutOffTimes]: "Chain-wide cut-off times",
  [P.chainsActivity]: "Chain activity log",
  [P.stores]: "Store list & details",
  [P.storesOperations]: "Operating hours & operational settings",
  [P.storesHealth]: "Readiness scorecards & settings diff",
  [P.storesHardware]: "Cameras & hardware",
  [P.storesDevices]: "Devices installed at the store",
  [P.storesSchedule]: "Image-capture schedule",
  [P.storesCutOffTimes]: "Store & inherited cut-off times",
  [P.storesMap]: "Store floor map / layout",
  [P.storesSettings]: "Store-level setting values",
  [P.storesActivity]: "Store activity log",
  [P.settings]: "Settings catalogue",
  [P.settingsChains]: "Chain-scoped setting values",
  [P.settingsStores]: "Store-scoped setting values",
  [P.settingsActivity]: "Settings activity log",
  [P.iamUsers]: "User accounts & access",
  [P.iamGroups]: "Permission groups",
  [P.iamVendors]: "Vendor accounts",
  [P.iamPermissions]: "Permission catalogue",
  [P.iamSso]: "SSO / identity providers",
  [P.ingressImports]: "Data import pipelines",
  [P.ingressTransformations]: "Import transformation definitions",
  [P.octoRules]: "Octo rule configuration",
  [P.octoVisualizer]: "Octo visualizer",
  [P.hardwareFirmware]: "Firmware versions",
  [P.hardwareDevices]: "Devices list",
};
